import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, CalendarDays } from "lucide-react";

function parseISO(s) { return new Date(s + "T00:00:00"); }
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
const WD_LONG = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

export default function VacationProtocol({ isAdmin, employees = [], currentUserId, currentUserName }) {
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [userId, setUserId] = useState(currentUserId);
  const [vacations, setVacations] = useState([]);
  const [company, setCompany] = useState([]);
  const [holidays, setHolidays] = useState({}); // {date: name}
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedName = useMemo(() => {
    if (userId === currentUserId) return currentUserName;
    return employees.find((e) => e.id === userId)?.name || "Mitarbeiter";
  }, [userId, employees, currentUserId, currentUserName]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const from = `${year}-01-01`;
        const to = `${year}-12-31`;
        const [calRes, statsRes, holRes] = await Promise.all([
          api.get("/vacations/calendar"),
          api.get(`/vacations/stats/${userId}`),
          api.get("/holidays", { params: { from, to } }),
        ]);
        if (!alive) return;
        const own = calRes.data.approved.filter((v) => v.user_id === userId);
        setVacations(own);
        setCompany(calRes.data.company || []);
        setStats(statsRes.data);
        const map = {};
        (holRes.data.holidays || []).forEach((h) => { map[h.date] = h.name; });
        setHolidays(map);
      } catch {
        // ignore
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [userId, year]);

  const rows = useMemo(() => {
    const out = [];
    const yStart = new Date(year, 0, 1);
    const yEnd = new Date(year, 11, 31);
    const push = (d, kind, reason) => {
      if (d.getFullYear() !== year) return;
      const dow = d.getDay();
      if (dow === 0 || dow === 6) return; // no weekends
      const iso = ymd(d);
      if (holidays[iso]) return; // no holidays
      out.push({
        date: iso,
        weekday: WD_LONG[dow],
        kind, // "Urlaub" | "Betriebsferien"
        reason: reason || "",
      });
    };
    vacations.forEach((v) => {
      const s = parseISO(v.start_date);
      const e = parseISO(v.end_date);
      const cur = new Date(Math.max(s.getTime(), yStart.getTime()));
      const stop = new Date(Math.min(e.getTime(), yEnd.getTime()));
      while (cur <= stop) {
        push(new Date(cur), "Urlaub", v.reason);
        cur.setDate(cur.getDate() + 1);
      }
    });
    company.forEach((c) => {
      const s = parseISO(c.start_date);
      const e = parseISO(c.end_date);
      const cur = new Date(Math.max(s.getTime(), yStart.getTime()));
      const stop = new Date(Math.min(e.getTime(), yEnd.getTime()));
      while (cur <= stop) {
        push(new Date(cur), "Betriebsferien", c.name);
        cur.setDate(cur.getDate() + 1);
      }
    });
    out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    return out;
  }, [vacations, company, holidays, year]);

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  const printProtocol = () => {
    window.print();
  };

  const totalUrlaub = rows.filter((r) => r.kind === "Urlaub").length;
  const totalBetriebs = rows.filter((r) => r.kind === "Betriebsferien").length;

  return (
    <div className="space-y-4">
      <div className="ph-card p-4 flex flex-col md:flex-row md:items-end gap-3 justify-between print:hidden">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-slate-500" />
          <div>
            <div className="ph-label">Urlaubs-Protokoll</div>
            <div className="text-sm text-slate-700">Jeder abgezogene Urlaubstag im Überblick</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="protocol-user-select" className="h-9 w-56">
                <SelectValue placeholder="Mitarbeiter wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={currentUserId}>{currentUserName} (ich)</SelectItem>
                {employees
                  .filter((e) => e.id !== currentUserId && e.is_active !== false)
                  .map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="protocol-year-select" className="h-9 w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={printProtocol}
            data-testid="protocol-print"
            className="h-9 px-3 rounded-md border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
          >
            Drucken / PDF
          </button>
        </div>
      </div>

      <div className="ph-card p-6 md:p-8 bg-white">
        {/* Kopfzeile im Rechnungs-Stil */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 border-b border-slate-200 pb-4">
          <div>
            <div className="font-display text-2xl font-semibold tracking-tight">Urlaubs-Protokoll {year}</div>
            <div className="text-sm text-slate-500 mt-0.5">Mitarbeiter: <span className="text-slate-800 font-medium">{selectedName}</span></div>
          </div>
          <div className="text-xs text-slate-400 text-right">
            Erstellt am {new Date().toLocaleDateString("de-DE")}<br />
            Kauz Logistik
          </div>
        </div>

        {/* Summary */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <SumBox label="Jahresanspruch" value={stats.total_days} suffix="Tage" tone="slate" />
            <SumBox label="Urlaub genommen" value={totalUrlaub} suffix="Tage" tone="sky" />
            <SumBox label="Betriebsferien" value={totalBetriebs} suffix="Tage" tone="amber" />
            <SumBox label="Resturlaub" value={stats.remaining_days} suffix="Tage" tone="teal" />
          </div>
        )}

        {/* Tabelle */}
        <div className="mt-6 border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left w-16">Lfd. Nr.</th>
                <th className="px-3 py-2 text-left">Datum</th>
                <th className="px-3 py-2 text-left">Wochentag</th>
                <th className="px-3 py-2 text-left">Art</th>
                <th className="px-3 py-2 text-left">Bemerkung</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Lade…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-slate-400">
                    <CalendarDays className="h-5 w-5 inline-block mr-1 -mt-1" />
                    Für {year} wurden noch keine Urlaubstage abgezogen.
                  </td>
                </tr>
              )}
              {!loading && rows.map((r, idx) => (
                <tr key={r.date + r.kind} data-testid={`protocol-row-${r.date}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-500 tabular-nums">{String(idx + 1).padStart(3, "0")}</td>
                  <td className="px-3 py-2 text-slate-800 font-medium tabular-nums">
                    {new Date(r.date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.weekday}</td>
                  <td className="px-3 py-2">
                    {r.kind === "Urlaub" ? (
                      <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-sky-50 text-sky-800 border border-sky-200">Urlaub</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">Betriebsferien</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{r.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
            {!loading && rows.length > 0 && (
              <tfoot className="bg-slate-50 text-xs text-slate-600">
                <tr className="border-t border-slate-200">
                  <td colSpan={4} className="px-3 py-2 text-right font-semibold uppercase tracking-wider">
                    Summe abgezogene Tage
                  </td>
                  <td className="px-3 py-2 text-slate-900 font-semibold tabular-nums">{rows.length}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <p className="text-[11px] text-slate-400 mt-4">
          Hinweis: Wochenenden und gesetzliche Feiertage werden nicht als Urlaubstag abgezogen.
          Bei Fragen wende dich bitte an {isAdmin ? "die Personalabteilung" : `deinen Chef (${user.role === "admin" ? "dich selbst" : "Admin"})`}.
        </p>
      </div>
    </div>
  );
}

function SumBox({ label, value, suffix, tone }) {
  const tones = {
    sky: "text-sky-700",
    teal: "text-teal-700",
    slate: "text-slate-700",
    amber: "text-amber-700",
  };
  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 flex items-end gap-1.5">
        <span className={`text-2xl font-semibold tabular-nums ${tones[tone] || "text-slate-700"}`}>{value ?? 0}</span>
        <span className="text-xs text-slate-500 mb-0.5">{suffix}</span>
      </div>
    </div>
  );
}
